import { __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef } from 'react';
// Notistack provides ability to stack Material UI snackbars. It is recommended on official Material UI website
// https://material-ui.com/components/snackbars/#notistack
import { SnackbarProvider as NotiStackSnackbarProvider, useSnackbar as useNotistackSnackbar, } from 'notistack';
import { CheckCircleIcon, ExclamationTriangleIcon, CloseIcon, IconButton, ExclamationCircleIcon, } from '../../material';
import { C2FOTheme, makeStyles } from '../../themes';
import { useMediaQuery } from '../../hooks';
const useStyles = makeStyles(({ palette, typography }) => ({
    contentRoot: {
        fontSize: typography.pxToRem(16),
    },
    action: {
        fontWeight: 700,
    },
    variantSuccess: {
        backgroundColor: `${palette.success.main}`,
    },
    variantError: {
        backgroundColor: `${palette.error.main}`,
    },
    variantWarning: {
        backgroundColor: `${palette.warning.main}`,
        '& > *': {
            color: palette.text.primary,
        },
    },
}));
export const SnackbarProvider = (_a) => {
    var { closeButtonAriaLabel, children } = _a, rest = __rest(_a, ["closeButtonAriaLabel", "children"]);
    const classes = useStyles();
    const isMobile = !useMediaQuery(C2FOTheme.breakpoints.up('sm'), { noSsr: true });
    const notistackRef = React.createRef();
    const onClickDismiss = (key) => () => {
        if (notistackRef.current) {
            notistackRef.current.closeSnackbar(key);
        }
    };
    const SnackbarIcon = ({ icon: Icon }) => (_jsx(Icon, { fontSize: "small", style: { marginRight: C2FOTheme.spacing(1) } }, void 0));
    return (_jsx(NotiStackSnackbarProvider, Object.assign({ iconVariant: {
            success: _jsx(SnackbarIcon, { icon: CheckCircleIcon }, void 0),
            error: _jsx(SnackbarIcon, { icon: ExclamationCircleIcon }, void 0),
            warning: _jsx(SnackbarIcon, { icon: ExclamationTriangleIcon }, void 0),
        }, classes: classes, ref: notistackRef, 
        // Dense is set to true for mobile devices to have less margins for your snackbars.
        dense: isMobile, action: (key) => (_jsx(IconButton, Object.assign({ size: "small", onClick: onClickDismiss(key), "aria-label": closeButtonAriaLabel !== null && closeButtonAriaLabel !== void 0 ? closeButtonAriaLabel : 'Close toaster', color: "inherit" }, { children: _jsx(CloseIcon, {}, void 0) }), void 0)) }, rest, { children: children }), void 0));
};
let snackbarKey = Date.now();
export function useSnackbar() {
    const alertSnackbarKey = useRef(Date.now());
    const { enqueueSnackbar, closeSnackbar } = useNotistackSnackbar();
    const openSnackbar = (message, options) => {
        // Default action provided on the prvider on line 26 is a close button. This can be replaced by passing action
        // option in OptionsObject. There isn't a straightforward way to remove action altogether. So for consumer's
        // simplicity if they pass action: false then I'm adding <></> to get rid of the action. Writing <></> at consumer
        // end isn't an intuitive way.
        const action = (options === null || options === void 0 ? void 0 : options.action) === false ? _jsx(_Fragment, {}, void 0) : options === null || options === void 0 ? void 0 : options.action;
        return enqueueSnackbar(message, Object.assign(Object.assign({}, options), { action }));
    };
    // This is also a Snackbar but with 2 special cases
    // 1. This will always be at top center(anchorOrigin: { horizontal: 'center', vertical: 'top' })
    // 2. This we not autoclose after 3 seconds(persist: true)
    const openSnackbarAlert = (message, options) => {
        const keyToClose = snackbarKey;
        snackbarKey += 1;
        // snackbarKey is being updated here to make sure we close previous Snackbar Alert before opening a new one.
        // This is to make sure only 1 Snackbar alert can be displayed at a time so that user don't get disturbed with
        // stacking of Snackbar Alerts on top-center of the screen
        closeSnackbar(keyToClose);
        return openSnackbar(message, Object.assign({ anchorOrigin: { horizontal: 'center', vertical: 'top' }, persist: true, key: snackbarKey }, options));
    };
    return { openSnackbar, openSnackbarAlert, closeSnackbar };
}
//# sourceMappingURL=SnackbarProvider.js.map